import { Box } from '@mui/material';

import { PLEASE_SELECT_QHP_OBJECT } from '../../../constants/visit';
import { useAppConfig } from '../../../fetching/api/appConfig';
import { Assignee, JointAssignment } from '../../../fetching/api/availability';
import { QhpSimple } from '../../../fetching/sharedSchemas/basicSchemas';
import { VisitOrderForDetailDisplay } from '../../../fetching/sharedSchemas/visitOrders';
import { useTransformedInputState } from '../../../utils/inputState';
import { ReturnComponent } from '../../../utils/reactTypes';
import { ServiceChips } from '../../displayFields/chips';
import { AxleLabel } from '../../formFields/AxleLabel';
import { VerticalStack } from '../../utils/linearLayouts';
import { AssigneesState, DumbAssigneeSelect, useAssigneesStateHelper } from './helpers';

// This is appropriate when creating a visit from a visit order,
//  where the positions aren't being added or removed.
export const useVisitOrderAssigneesState = (visitOrder: VisitOrderForDetailDisplay) => {
    const assignment: JointAssignment = Object.fromEntries(
        visitOrder.service_bundle_detail.positions.map((position) => [
            position.uuid,
            PLEASE_SELECT_QHP_OBJECT,
        ]),
    );
    return useAssigneesStateHelper({
        assignment,
        positionIdsToForceReassign: [],
        onChangeCallbacks: [],
    });
};

export const DumbVisitPositionAssigneeField = ({
    positionId,
    assigneesState,
    assignableQhps,
    eligibleQhps,
    availableQhps,
    enabled,
}: {
    positionId: string;
    assigneesState: AssigneesState;
    assignableQhps: QhpSimple[];
    eligibleQhps: QhpSimple[];
    availableQhps: QhpSimple[] | null;
    enabled?: boolean;
}): ReturnComponent => {
    const assigneeState = useTransformedInputState<JointAssignment, Assignee>({
        inputState: assigneesState,
        // TODO: Figure out why ESLint thinks the index access will always work.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        transform: (newAssignment) => newAssignment[positionId] ?? PLEASE_SELECT_QHP_OBJECT,
        untransform: (newQhp) => ({ ...assigneesState.value, [positionId]: newQhp }),
    });
    return (
        <DumbAssigneeSelect
            label={''}
            careTeam={[]}
            inputState={assigneeState}
            assignableQhps={assignableQhps}
            eligibleQhps={eligibleQhps}
            availableQhps={availableQhps}
            enabled={enabled}
        />
    );
};

export const SmartPotentialVisitAssigneesField = ({
    label,
    assignableQhps,
    eligibleQhpsByPositionId,
    availableQhpsByPositionId,
    assigneesState,
    positions,
}: {
    label: string;
    assignableQhps: QhpSimple[];
    eligibleQhpsByPositionId: Record<string, QhpSimple[]>;
    availableQhpsByPositionId: Record<string, QhpSimple[]> | null;
    assigneesState: AssigneesState;
    positions: { uuid: string; services: { name: string }[] }[];
}): ReturnComponent => {
    // Queries.
    const appConfig = useAppConfig();

    // Rendering.
    return (
        <>
            <AxleLabel label={label} required={true} />
            <VerticalStack spacing={1}>
                {positions.map((position) => (
                    <VerticalStack key={position.uuid}>
                        {appConfig.enable_multiposition && (
                            <Box mb={1}>
                                <ServiceChips services={position.services} />
                            </Box>
                        )}
                        <DumbVisitPositionAssigneeField
                            positionId={position.uuid}
                            assigneesState={assigneesState}
                            assignableQhps={assignableQhps}
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            eligibleQhps={eligibleQhpsByPositionId?.[position.uuid] ?? []}
                            availableQhps={availableQhpsByPositionId?.[position.uuid] ?? null}
                        />
                    </VerticalStack>
                ))}
            </VerticalStack>
        </>
    );
};
